import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  IconButton,
  InputAdornment,
  Paper,
  Box,
  CircularProgress
} from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  Close as CloseIcon,
  FileCopy,
  Assignment,
  Speed,
  Chat,
  Apps
} from "@material-ui/icons";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import ButtonWithSpinner from "../ButtonWithSpinner";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    backgroundColor: theme.palette.type === "dark" ? "#16191E" : "#FFFFFF",
    border: theme.palette.type === "dark" ? "1px solid rgba(0, 229, 255, 0.25)" : "1px solid rgba(0, 0, 0, 0.12)",
    borderRadius: 14,
    color: theme.palette.type === "dark" ? "#FFFFFF" : "#1E293B",
    boxShadow: "0 20px 50px rgba(0, 0, 0, 0.8)",
  },
  dialogTitle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottom: theme.palette.type === "dark" ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(0, 0, 0, 0.08)",
    padding: theme.spacing(2, 3),
  },
  titleText: {
    fontSize: "1.1rem",
    fontWeight: 700,
    color: theme.palette.primary.main,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  formRoot: {
    display: "flex",
    flexDirection: "column",
    maxHeight: "85vh",
    overflow: "hidden",
  },
  dialogContent: {
    padding: theme.spacing(2.5, 3),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  sectionCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    borderRadius: 10,
    backgroundColor: theme.palette.type === "dark" ? "#111418" : "#F8FAFC",
    border: theme.palette.type === "dark" ? "1px solid rgba(255, 255, 255, 0.06)" : "1px solid rgba(0, 0, 0, 0.06)",
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
    borderBottom: theme.palette.type === "dark" ? "1px solid rgba(255, 255, 255, 0.05)" : "1px solid rgba(0, 0, 0, 0.05)",
    paddingBottom: theme.spacing(1),
  },
  sectionTitle: {
    fontSize: "0.85rem",
    fontWeight: 700,
    letterSpacing: "0.5px",
    color: theme.palette.primary.main,
    textTransform: "uppercase",
  },
  sectionIcon: {
    color: theme.palette.primary.main,
    fontSize: "1.2rem",
  },
  dialogActions: {
    padding: theme.spacing(2, 3),
    borderTop: theme.palette.type === "dark" ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(0, 0, 0, 0.08)",
    gap: theme.spacing(1.5),
  },
  actionButton: {
    borderRadius: 8,
    fontWeight: 700,
    fontSize: "0.88rem",
    textTransform: "none",
    padding: "8px 26px",
    backgroundColor: `${theme.palette.primary.main} !important`,
    color: `${theme.palette.primary.contrastText || "#ffffff"} !important`,
    boxShadow: `0 0 14px ${theme.palette.primary.main}66`,
    transition: "all 0.25s ease",
    "&:hover": {
      backgroundColor: `${theme.palette.primary.main}CC !important`,
      boxShadow: `0 0 18px ${theme.palette.primary.main}99`,
    },
    "&:disabled": {
      backgroundColor: "rgba(255, 255, 255, 0.12) !important",
      color: "rgba(255, 255, 255, 0.3) !important",
      boxShadow: "none",
    },
  },
  outlineButton: {
    borderColor: `${theme.palette.primary.main}80`,
    color: theme.palette.primary.main,
    fontWeight: 600,
    borderRadius: 8,
    textTransform: "none",
    padding: "6px 20px",
    fontSize: "0.88rem",
    transition: "all 0.25s ease",
    "&:hover": {
      borderColor: theme.palette.primary.main,
      backgroundColor: `${theme.palette.primary.main}14`,
      boxShadow: "0 0 10px rgba(0, 229, 255, 0.2)",
    },
  },
  deleteButton: {
    borderColor: "#ff5252",
    color: "#ff5252",
    fontWeight: 600,
    borderRadius: 8,
    textTransform: "none",
    padding: "6px 16px",
    fontSize: "0.88rem",
    marginRight: "auto",
    "&:hover": {
      borderColor: "#ff1744",
      backgroundColor: "rgba(255, 82, 82, 0.08)",
      boxShadow: "0 0 10px rgba(255, 82, 82, 0.2)",
    },
  },
}));

export default function PlanModal({
  open,
  onClose,
  initialValue,
  onSubmit,
  onDelete,
  loading
}) {
  const classes = useStyles();
  const theme = useTheme();

  const [record, setRecord] = useState({
    name: "",
    users: 0,
    connections: 0,
    maxBrands: 1,
    maxFunnels: 1,
    maxConnQr: 1,
    maxConnApi: 1,
    maxConnIg: 1,
    maxConnMessenger: 1,
    maxConnTelegram: 1,
    maxConnEmail: 1,
    queues: 0,
    maxContacts: 0,
    maxFlows: 0,
    messagesLimit: 0,
    maxStatusPosts: 0,
    maxGroupPosts: 0,
    maxCommunityPosts: 0,
    maxWebWidgets: 0,
    amount: 0,
    useWhatsapp: true,
    useFacebook: true,
    useInstagram: true,
    useCampaigns: true,
    useSchedules: true,
    useInternalChat: true,
    useExternalApi: true,
    useKanban: true,
    useOpenAi: true,
    useIntegrations: true,
    isPublic: true,
    useWhatsappOfficial: false,
    trial: false,
    trialDays: 3,
    recurrence: "monthly",
    wavoip: false,
    paymentLink: ""
  });

  useEffect(() => {
    if (!initialValue) return;
    setRecord((prev) => ({
      ...prev,
      ...initialValue,
      maxConnEmail:
        initialValue.maxConnEmail !== undefined &&
        initialValue.maxConnEmail !== null
          ? initialValue.maxConnEmail
          : prev.maxConnEmail ?? 1,
      maxFlows: initialValue.maxFlows ?? prev.maxFlows ?? 0,
      messagesLimit: initialValue.messagesLimit ?? prev.messagesLimit ?? 0,
      maxStatusPosts: initialValue.maxStatusPosts ?? prev.maxStatusPosts ?? 0,
      maxGroupPosts: initialValue.maxGroupPosts ?? prev.maxGroupPosts ?? 0,
      maxCommunityPosts:
        initialValue.maxCommunityPosts ?? prev.maxCommunityPosts ?? 0,
      maxWebWidgets: initialValue.maxWebWidgets ?? prev.maxWebWidgets ?? 0,
    }));
  }, [initialValue]);

  const handleFormSubmit = async (data) => {
    data.useWhatsapp = Number(data.maxConnQr) > 0;
    data.useWhatsappOfficial = Number(data.maxConnApi) > 0;
    data.useFacebook = Number(data.maxConnMessenger) > 0;
    data.useInstagram = Number(data.maxConnIg) > 0;
    data.connections =
      (Number(data.maxConnQr) || 0) +
      (Number(data.maxConnApi) || 0) +
      (Number(data.maxConnIg) || 0) +
      (Number(data.maxConnMessenger) || 0) +
      (Number(data.maxConnTelegram) || 0);
    data.maxConnEmail = Number(data.maxConnEmail) || 0;
    data.maxFlows = Number(data.maxFlows) || 0;
    data.messagesLimit = Number(data.messagesLimit) || 0;
    data.maxStatusPosts = Number(data.maxStatusPosts) || 0;
    data.maxGroupPosts = Number(data.maxGroupPosts) || 0;
    data.maxCommunityPosts = Number(data.maxCommunityPosts) || 0;
    data.maxWebWidgets = Number(data.maxWebWidgets) || 0;
    data.useSchedules = true;
    data.useInternalChat = true;
    data.useExternalApi = true;
    data.useKanban = true;
    data.useOpenAi = true;
    data.useIntegrations = true;
    data.trial = data.trial === true || data.trial === "true";
    data.trialDays = data.trial ? Number(data.trialDays) || 0 : 0;
    await onSubmit(data);
  };

  const isEditing = Boolean(record && record.id);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ className: classes.dialogPaper }}
    >
      <div className={classes.dialogTitle}>
        <Typography className={classes.titleText}>
          <Assignment style={{ color: theme.palette.primary.main, fontSize: "1.4rem" }} />
          {isEditing
            ? i18n.t("plans.modal.titleEdit", { name: record.name })
            : i18n.t("plans.modal.titleNew")}
        </Typography>
        <IconButton size="small" onClick={onClose} style={{ color: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.7)" : "#64748B" }}>
          <CloseIcon />
        </IconButton>
      </div>

      <Formik
        enableReinitialize
        initialValues={record}
        onSubmit={async (values) => {
          await handleFormSubmit(values);
        }}
      >
        {({ values, handleChange }) => (
          <Form className={classes.formRoot}>
            <DialogContent className={classes.dialogContent} dividers>
              {/* 1. INFORMACIÓN GENERAL Y FACTURACIÓN */}
              <Paper className={classes.sectionCard} elevation={0}>
                <div className={classes.sectionHeader}>
                  <Assignment className={classes.sectionIcon} />
                  <Typography className={classes.sectionTitle}>
                    {i18n.t("plans.sections.general")}
                  </Typography>
                </div>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Field
                      as={TextField}
                      label={i18n.t("plans.form.name")}
                      name="name"
                      variant="outlined"
                      size="small"
                      fullWidth
                      required
                    />
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <FormControl variant="outlined" size="small" fullWidth>
                      <InputLabel id="isPublic-label">
                        {i18n.t("plans.form.status")}
                      </InputLabel>
                      <Field
                        as={Select}
                        labelId="isPublic-label"
                        label={i18n.t("plans.form.status")}
                        name="isPublic"
                      >
                        <MenuItem value={true}>{i18n.t("plans.form.active")}</MenuItem>
                        <MenuItem value={false}>{i18n.t("plans.form.inactive")}</MenuItem>
                      </Field>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <FormControl variant="outlined" size="small" fullWidth>
                      <InputLabel id="trial-label">
                        {i18n.t("plans.form.trial")}
                      </InputLabel>
                      <Field
                        as={Select}
                        labelId="trial-label"
                        label={i18n.t("plans.form.trial")}
                        name="trial"
                      >
                        <MenuItem value={true}>{i18n.t("plans.form.yes")}</MenuItem>
                        <MenuItem value={false}>{i18n.t("plans.form.no")}</MenuItem>
                      </Field>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <Field
                      as={TextField}
                      label={i18n.t("plans.form.trialDays")}
                      name="trialDays"
                      variant="outlined"
                      size="small"
                      type="number"
                      inputProps={{ min: 0 }}
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <Field
                      as={TextField}
                      label={i18n.t("plans.form.amount")}
                      name="amount"
                      variant="outlined"
                      size="small"
                      type="text"
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Field
                      as={TextField}
                      label={i18n.t("plans.form.customPaymentLink")}
                      name="paymentLink"
                      variant="outlined"
                      size="small"
                      placeholder="https://mercadopago..."
                      fullWidth
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => {
                                navigator.clipboard.writeText(values.paymentLink || "");
                                toast.success(i18n.t("plans.form.linkCopied", "Enlace copiado"));
                              }}
                              size="small"
                              style={{ color: theme.palette.primary.main }}
                            >
                              <FileCopy style={{ fontSize: "16px" }} />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              </Paper>

              {/* 2. LÍMITES DE RECURSOS Y CONSUMO */}
              <Paper className={classes.sectionCard} elevation={0}>
                <div className={classes.sectionHeader}>
                  <Speed className={classes.sectionIcon} />
                  <Typography className={classes.sectionTitle}>
                    {i18n.t("plans.sections.limits")}
                  </Typography>
                </div>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Field
                      as={TextField}
                      label={i18n.t("plans.form.users")}
                      name="users"
                      variant="outlined"
                      size="small"
                      type="number"
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <Field
                      as={TextField}
                      label={i18n.t("plans.form.brands")}
                      name="maxBrands"
                      variant="outlined"
                      size="small"
                      type="number"
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <Field
                      as={TextField}
                      label={i18n.t("plans.form.funnels")}
                      name="maxFunnels"
                      variant="outlined"
                      size="small"
                      type="number"
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <Field
                      as={TextField}
                      label={i18n.t("plans.form.queues")}
                      name="queues"
                      variant="outlined"
                      size="small"
                      type="number"
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <Field
                      as={TextField}
                      label={i18n.t("plans.form.maxContacts")}
                      name="maxContacts"
                      variant="outlined"
                      size="small"
                      type="number"
                      helperText={i18n.t("plans.form.zeroIsUnlimited")}
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <Field
                      as={TextField}
                      label={i18n.t("plans.form.maxFlows")}
                      name="maxFlows"
                      variant="outlined"
                      size="small"
                      type="number"
                      helperText={i18n.t("plans.form.zeroIsUnlimited")}
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <Field
                      as={TextField}
                      label={i18n.t("plans.form.messagesLimit")}
                      name="messagesLimit"
                      variant="outlined"
                      size="small"
                      type="number"
                      helperText={i18n.t("plans.form.zeroIsUnlimited")}
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <Field
                      as={TextField}
                      label={i18n.t("plans.form.maxStatusPosts")}
                      name="maxStatusPosts"
                      variant="outlined"
                      size="small"
                      type="number"
                      helperText={i18n.t("plans.form.postsHelp")}
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <Field
                      as={TextField}
                      label={i18n.t("plans.form.maxGroupPosts")}
                      name="maxGroupPosts"
                      variant="outlined"
                      size="small"
                      type="number"
                      helperText={i18n.t("plans.form.postsHelp")}
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <Field
                      as={TextField}
                      label={i18n.t("plans.form.maxCommunityPosts")}
                      name="maxCommunityPosts"
                      variant="outlined"
                      size="small"
                      type="number"
                      helperText={i18n.t("plans.form.postsHelp")}
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <Field
                      as={TextField}
                      label={i18n.t("plans.form.maxWebWidgets")}
                      name="maxWebWidgets"
                      variant="outlined"
                      size="small"
                      type="number"
                      helperText={i18n.t("plans.form.postsHelp")}
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </Paper>

              {/* 3. CANALES DE CONEXIÓN HABILITADOS */}
              <Paper className={classes.sectionCard} elevation={0}>
                <div className={classes.sectionHeader}>
                  <Chat className={classes.sectionIcon} />
                  <Typography className={classes.sectionTitle}>
                    {i18n.t("plans.sections.channels")}
                  </Typography>
                </div>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={4}>
                    <Field
                      as={TextField}
                      label={i18n.t("plans.form.connQr")}
                      name="maxConnQr"
                      variant="outlined"
                      size="small"
                      type="number"
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={6} sm={4}>
                    <Field
                      as={TextField}
                      label={i18n.t("plans.form.connApi")}
                      name="maxConnApi"
                      variant="outlined"
                      size="small"
                      type="number"
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={6} sm={4}>
                    <Field
                      as={TextField}
                      label={i18n.t("plans.form.connIg")}
                      name="maxConnIg"
                      variant="outlined"
                      size="small"
                      type="number"
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={6} sm={4}>
                    <Field
                      as={TextField}
                      label={i18n.t("plans.form.connFb")}
                      name="maxConnMessenger"
                      variant="outlined"
                      size="small"
                      type="number"
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={6} sm={4}>
                    <Field
                      as={TextField}
                      label={i18n.t("plans.form.connTg")}
                      name="maxConnTelegram"
                      variant="outlined"
                      size="small"
                      type="number"
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={6} sm={4}>
                    <Field
                      as={TextField}
                      label={i18n.t("plans.form.connEmail")}
                      name="maxConnEmail"
                      variant="outlined"
                      size="small"
                      type="number"
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </Paper>

              {/* 4. MÓDULOS ADICIONALES Y TELEFONÍA */}
              <Paper className={classes.sectionCard} elevation={0}>
                <div className={classes.sectionHeader}>
                  <Apps className={classes.sectionIcon} />
                  <Typography className={classes.sectionTitle}>
                    {i18n.t("plans.sections.modules")}
                  </Typography>
                </div>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControl variant="outlined" size="small" fullWidth>
                      <InputLabel id="useCampaigns-label">
                        {i18n.t("plans.form.campaigns")}
                      </InputLabel>
                      <Field
                        as={Select}
                        labelId="useCampaigns-label"
                        label={i18n.t("plans.form.campaigns")}
                        name="useCampaigns"
                      >
                        <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                        <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                      </Field>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl variant="outlined" size="small" fullWidth>
                      <InputLabel id="wavoip-label">
                        {i18n.t("plans.form.wavoip")}
                      </InputLabel>
                      <Field
                        as={Select}
                        labelId="wavoip-label"
                        label={i18n.t("plans.form.wavoip")}
                        name="wavoip"
                      >
                        <MenuItem value={true}>{i18n.t("plans.form.enabled")}</MenuItem>
                        <MenuItem value={false}>{i18n.t("plans.form.disabled")}</MenuItem>
                      </Field>
                    </FormControl>
                  </Grid>
                </Grid>
              </Paper>
            </DialogContent>

            <DialogActions className={classes.dialogActions}>
              {isEditing && (
                <ButtonWithSpinner
                  className={classes.deleteButton}
                  variant="outlined"
                  onClick={() => onDelete(record)}
                  loading={loading}
                >
                  {i18n.t("plans.buttons.delete")}
                </ButtonWithSpinner>
              )}
              <ButtonWithSpinner
                className={classes.outlineButton}
                variant="outlined"
                onClick={onClose}
                disabled={loading}
              >
                {i18n.t("plans.buttons.cancel")}
              </ButtonWithSpinner>
              <ButtonWithSpinner
                className={classes.actionButton}
                type="submit"
                loading={loading}
              >
                {i18n.t("plans.buttons.save")}
              </ButtonWithSpinner>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
}
